import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, HostBinding, Output, Signal, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { Store } from '@ngrx/store';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { take } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { RewardTokensActions } from '../../actions';
import { AcquisitionContextType, IOnboardingViewData, IRewardToken } from '../../reward-tokens.model';
import { AcquisitionRewardTokenService } from '../../services/acquisition-reward-token.service';
import { RewardTokensOnboardingComponent } from './reward-tokens-onboarding.component';

@Component({
    selector: 'bs-acquisition-reward-onboarding-container',
    templateUrl: './acquisition-reward-onboarding-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./acquisition-reward-onboarding-container.component.scss'],
    imports: [RewardTokensOnboardingComponent],
})
export class AcquisitionRewardOnboardingContainerComponent {
    @HostBinding('class') class = 'reward-tokens-module';

    private isEpcotEnabled = this.epcotConfigService.isEnabled(EpcotModule.Rewards);
    private readonly destroyRef = inject(DestroyRef);

    selectedToken = input.required<IRewardToken>();
    displayAddWelcomeOfferTooltip = input<boolean>(false);
    notificationShown: Signal<boolean>;
    @Output() hideAddWelcomeOfferTooltip = new EventEmitter<void>();

    onboardingData: Signal<IOnboardingViewData | undefined>;

    constructor(
        public sitecore: Sitecore,
        private tokensConfig: CrmConfig,
        private epcotConfigService: EpcotConfigService,
        private timerService: TimerService,
        private store: Store<IBetslipRootState>,
        private acquisitionRewardTokenService: AcquisitionRewardTokenService,
    ) {
        const localStore = this.acquisitionRewardTokenService.getAcquisitionRewardNotification();
        this.notificationShown = computed(() =>
            this.displayAddWelcomeOfferTooltip() ? localStore.AddWelcomeOfferNotificationShown : localStore.autoApplyNotificationShown,
        );

        this.onboardingData = computed(() => (this.notificationShown() ? undefined : this.getOnboardingData(this.selectedToken().rewardTokenType)));

        if (!this.notificationShown()) {
            this.timerService
                .timer(this.tokensConfig.tokens.acquisitionRewardDetails.autoApplyTooltipDuration)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.hideOnboarding());
        }
    }

    hideOnboarding(): void {
        if (this.displayAddWelcomeOfferTooltip()) {
            this.hideAddWelcomeOfferTooltip.emit();
            this.acquisitionRewardTokenService.storeAcquisitionRewardNotification(AcquisitionContextType.AddWelcomeOffer);
        } else {
            this.store.dispatch(RewardTokensActions.hideAcquisitionOnboarding());
            this.acquisitionRewardTokenService.storeAcquisitionRewardNotification(AcquisitionContextType.AutoApplyTooltip);
        }
    }

    private getOnboardingData(tokenType: RewardTokenType): IOnboardingViewData {
        const title = this.isEpcotEnabled ? '' : this.sitecore.crmTokens.messages.WelcomeOfferTitle;
        const icon = this.isEpcotEnabled ? '' : this.acquisitionRewardTokenService.getTokenIcon(tokenType);
        const content = this.displayAddWelcomeOfferTooltip()
            ? this.sitecore.crmTokens.tokensOnboardingMessages.AddWelcomeOfferContent
            : this.sitecore.crmTokens.tokensOnboardingMessages.WelcomeOfferContent;

        return <IOnboardingViewData>{
            title,
            content,
            icon,
        };
    }
}
